import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { urgentCaseAPI } from '../../services/api';

const ActiveUrgentCasesBanner = () => {
  const { t } = useTranslation();
  const [activeCases, setActiveCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveCases();
  }, []);

  const fetchActiveCases = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await urgentCaseAPI.getActiveUrgentCases();
      
      if (response.data.status === 'success') {
        setActiveCases(response.data.data || []);
      } else {
        setError(response.data.message || t("emergency.banner.fetchFailed"));
      }
    } catch (err) {
      console.error('Error fetching active urgent cases:', err);
      setError(t("emergency.banner.loadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewCase = (caseId) => {
    navigate(`/medical/emergency-broadcast/${caseId}`);
  };

  const handleCloseCase = async (caseId) => {
    try {
      await urgentCaseAPI.endCase(caseId);
      // Refresh the list after closing a case
      fetchActiveCases();
    } catch (err) {
      console.error('Error closing case:', err);
      setError(t("emergency.banner.closeFailed"));
    }
  };

  if (isLoading) {
    return (
      <div style={{
        backgroundColor: '#FEF3C7',
        border: '1px solid #F59E0B',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid #F59E0B',
          borderTop: '2px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <span style={{
          color: '#92400E',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: '"Red Hat Display", sans-serif'
        }}>
          {t("emergency.banner.loading")}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#FEF2F2',
        border: '1px solid #FECACA',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span style={{
          color: '#DC2626',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: '"Red Hat Display", sans-serif'
        }}>
          {error}
        </span>
        <button
          onClick={fetchActiveCases}
          style={{
            marginLeft: 'auto',
            padding: '4px 8px',
            backgroundColor: '#EF4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          {t("emergency.banner.retry")}
        </button>
      </div>
    );
  }

  if (activeCases.length === 0) {
    return null; // Don't show banner if no active cases
  }

  return (
    <div style={{
      backgroundColor: '#FEF2F2',
      border: '2px solid #FECACA',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '20px',
      boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.1), 0 2px 4px -1px rgba(239, 68, 68, 0.06)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#FECACA',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        </div>
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 4px 0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("emergency.banner.title")}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: '0',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("emergency.banner.activeCount", { count: activeCases.length })}
          </p>
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {activeCases.map((urgentCase) => (
          <div
            key={urgentCase.id}
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #FECACA',
              borderRadius: '12px',
              padding: '16px',
              transition: 'all 0.2s ease'
            }}
          >
            {/* Header with status and case info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{
                  backgroundColor: urgentCase.status === 'pending' ? '#FEF3C7' : '#D1FAE5',
                  color: urgentCase.status === 'pending' ? '#92400E' : '#065F46',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  fontFamily: '"Red Hat Display", sans-serif',
                  textTransform: 'uppercase'
                }}>
                  {t(`emergency.status.${urgentCase.status}`)}
                </span>
                <span style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  {urgentCase.case_ref}
                </span>
                {urgentCase.remaining_time_in_seconds && (
                  <span style={{
                    fontSize: '12px',
                    color: '#EF4444',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12,6 12,12 16,14"/>
                    </svg>
                    {Math.floor(urgentCase.remaining_time_in_seconds / 60)}m {t("emergency.banner.remaining")}
                  </span>
                )}
              </div>
            </div>

            {/* Problem Description */}
            <div style={{ marginBottom: '12px' }}>
              <p style={{
                fontSize: '14px',
                color: '#1F2937',
                margin: '0 0 8px 0',
                fontFamily: '"Red Hat Display", sans-serif',
                lineHeight: '1.4'
              }}>
                {urgentCase.problem_description}
              </p>
            </div>

            {/* Pet Information */}
            {urgentCase.pets && urgentCase.pets.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                <span style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  {urgentCase.pets.map(pet => `${pet.name} (${pet.species}, ${pet.age})`).join(', ')}
                </span>
              </div>
            )}

            {/* Media Preview */}
            {urgentCase.media && urgentCase.media.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21,15 16,10 5,21"/>
                  </svg>
                  <span style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: '500'
                  }}>
                    {t("emergency.banner.mediaReady", { count: urgentCase.media.length })}
                  </span>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  paddingBottom: '4px'
                }}>
                  {urgentCase.media.slice(0, 3).map((media, index) => (
                    <div
                      key={media.id}
                      style={{
                        minWidth: '60px',
                        height: '60px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#F3F4F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #E5E7EB'
                      }}
                    >
                      {media.is_image ? (
                        <img
                          src={media.url}
                          alt={media.file_name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="23 7 16 12 23 17 23 7"/>
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                        </svg>
                      )}
                    </div>
                  ))}
                  {urgentCase.media.length > 3 && (
                    <div style={{
                      minWidth: '60px',
                      height: '60px',
                      borderRadius: '8px',
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E5E7EB',
                      fontSize: '12px',
                      color: '#6B7280',
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontWeight: '500'
                    }}>
                      +{urgentCase.media.length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Doctor Assignment */}
            {urgentCase.doctor && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                <span style={{
                  fontSize: '12px',
                  color: '#10B981',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: '500'
                }}>
                  {t("emergency.banner.assignedTo", { name: `${urgentCase.doctor.first_name} ${urgentCase.doctor.last_name}` })}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => handleViewCase(urgentCase.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#EF4444';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t("emergency.banner.viewCase")}
              </button>
              
              <button
                onClick={() => handleCloseCase(urgentCase.id)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#F9FAFB';
                  e.target.style.borderColor = '#9CA3AF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#D1D5DB';
                }}
              >
                {t("emergency.banner.closeCase")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActiveUrgentCasesBanner;
